// Features page — tenant config toggle management.
// Shows feature toggles with tier-gating: toggles not permitted by the current
// tier are visually disabled with an explanation.
// Admin-only (backend enforces requireTenantAdmin).

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi, ApiRequestError } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import {
  platformApi,
  TOGGLE_DEFINITIONS,
  type TenantConfigToggles,
  type UpdateTenantConfigRequest,
} from '@/lib/api/platform'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { FormSkeleton } from '@/components/shared/LoadingSkeleton'
import { Card, CardContent } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import { useToast } from '@/lib/hooks/use-toast'
import { Loader2, Lock } from 'lucide-react'

export function FeaturesPage() {
  const { t } = useTranslation(['platform', 'common'])
  const api = useApi()
  const { slug } = useTenant()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const pApi = platformApi(api)

  // Local state for pending changes
  const [localToggles, setLocalToggles] = useState<TenantConfigToggles | null>(null)
  const [isDirty, setIsDirty] = useState(false)

  const {
    data: config,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: queryKeys.platform.tenantConfig(slug),
    queryFn: () => pApi.getTenantConfig(),
    retry: false,
  })

  // Fetch subscription for tier display name (used in "not available" messages)
  const { data: subscription } = useQuery({
    queryKey: queryKeys.platform.subscription(slug),
    queryFn: () => pApi.getSubscription(),
    retry: false,
  })

  // Sync local state when data loads (only once, or when not dirty)
  if (config && !localToggles && !isDirty) {
    setLocalToggles({ ...config.toggles })
  }

  const saveMutation = useMutation({
    mutationFn: (data: UpdateTenantConfigRequest) => pApi.updateTenantConfig(data),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.platform.tenantConfig(slug) })
      // Also invalidate entitlements since the effective entitlements may have changed
      queryClient.invalidateQueries({ queryKey: [queryKeys.entitlements(slug)] })
      setLocalToggles({ ...updated })
      setIsDirty(false)
      toast({ title: t('features.saved') })
    },
    onError: (err) => {
      toast({
        title: t('features.saveFailed'),
        description: err instanceof ApiRequestError ? err.message : t('common:errors.generic'),
        variant: 'destructive',
      })
    },
  })

  function handleToggle(key: keyof TenantConfigToggles, checked: boolean) {
    setLocalToggles((prev) => (prev ? { ...prev, [key]: checked } : prev))
    setIsDirty(true)
  }

  function handleSave() {
    if (!localToggles) return
    saveMutation.mutate(localToggles)
  }

  function handleReset() {
    if (config) {
      setLocalToggles({ ...config.toggles })
      setIsDirty(false)
    }
  }

  /** Check if a toggle is permitted by the tier ceiling (Layer 1). */
  function isTierPermitted(entitlementKey: string): boolean {
    if (!config) return false
    const value = config.tier_entitlements[entitlementKey]
    return value === true
  }

  function getTierDisplayName(): string {
    return subscription?.tier_display_name ?? 'your current'
  }

  if (isLoading) return <FormSkeleton />

  if (error) {
    return (
      <ErrorState
        message={t('common:errors.loadFailed', { resource: t('features.title') })}
        technicalDetail={error instanceof ApiRequestError ? error.message : undefined}
        onRetry={() => refetch()}
      />
    )
  }

  if (!config || !localToggles) return null

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{t('features.title')}</h1>
        <p className="text-muted-foreground">{t('features.description')}</p>
      </div>

      <Card>
        <CardContent className="divide-y pt-6">
          {TOGGLE_DEFINITIONS.map((toggle) => {
            const permitted = isTierPermitted(toggle.entitlementKey)
            const currentValue = localToggles[toggle.key]

            return (
              <div
                key={toggle.key}
                className={`flex items-center justify-between py-4 first:pt-0 last:pb-0 ${
                  !permitted ? 'opacity-60' : ''
                }`}
              >
                <div className="space-y-0.5 pr-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{t(toggle.labelKey)}</span>
                    {!permitted && <Lock className="h-3.5 w-3.5 text-muted-foreground" />}
                  </div>
                  <p className="text-sm text-muted-foreground">{t(toggle.descriptionKey)}</p>
                  {!permitted && (
                    <p className="text-xs text-muted-foreground">
                      {t('features.tierBlocked', { tier: getTierDisplayName() })}
                    </p>
                  )}
                </div>
                <Switch
                  checked={currentValue}
                  onCheckedChange={(checked) => handleToggle(toggle.key, checked)}
                  disabled={!permitted || saveMutation.isPending}
                  aria-label={t(toggle.labelKey)}
                />
              </div>
            )
          })}
        </CardContent>
      </Card>

      <div className="flex items-center gap-4">
        <Button onClick={handleSave} disabled={!isDirty || saveMutation.isPending}>
          {saveMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t('common:saving')}
            </>
          ) : (
            t('common:save')
          )}
        </Button>
        {isDirty && (
          <Button variant="outline" onClick={handleReset} disabled={saveMutation.isPending}>
            {t('common:cancel')}
          </Button>
        )}
      </div>
    </div>
  )
}
